import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useStore } from '@shared/hooks/useStore';
import { Button } from '@shared/components/common/Button';
import { PencilLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from '@/shared/configs/constants';
import { ChangePasswordPage } from '@user/pages';

const InfoRow = ({ label, value, helperText }: { label: string, value?: React.ReactNode, helperText?: string }) => {
    return (
        <div className='flex flex-col md:flex-row'>
            <div className='w-full md:w-1/3 min-w-[160px] text-[#999999] mb-1 md:mb-0'>
                {label}
            </div>
            <div className='flex-1'>
                {value || '--'}
                {helperText && <p className='text-danger text-sm'>{helperText}</p>}
            </div>
        </div>
    );
};

export default observer(function AdminProfilePage() {
    const {
        modalStore: { showModal },
        profileStore: { profile, getUserProfile }
    } = useStore();

    useEffect(() => {
        getUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showChangePassword = () => {
        showModal({
            id: 'form-change-password',
            size: '2xl',
            showHeader: false,
            footerClassName: 'hidden',
            content: <ChangePasswordPage />
        });
    };

    if (!profile) return null;

    return (
        <div className='space-y-6'>
            {/* Header Action */}
            <div className='flex justify-end'>
                <Link to={ADMIN_ROUTES.profileEdit.href}>
                    <Button className='gap-2 bg-primary text-white rounded-[10px] px-[10px] py-[14px] font-semibold leading-[16px]'>
                        <PencilLine size={16} />
                        プロフィール編集
                    </Button>
                </Link>
            </div>

            {/* Personal Information */}
            <section className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-6'>
                <h2 className='text-[20px] leading-[36px] font-semibold mb-6'>個人情報</h2>
                <div className='space-y-6'>
                    <InfoRow label='名前' value={profile.fullName} />
                    <InfoRow label='名前（フリガナ）' value={profile.fullNameFuri} />
                    <InfoRow label='メールアドレス' value={profile.email} helperText={!!profile?.newEmail ? `∗ メールアドレスを${profile?.newEmail}に変更するように要求しました 。新しいメールアドレスの確認リンクを記載したメールが届きます。再ログインする前に、このメールを開いてリンクをクリックしてください。` : undefined} />
                    {!profile.isSynchronizedRecord && (
                        <InfoRow
                            label='パスワード'
                            value={(
                                <div className='cursor-pointer font-semibold text-[#009ACD] underline-offset-2 hover:underline' onClick={() => showChangePassword()}>
                                    パスワード変更
                                </div>
                            )}
                        />
                    )}
                </div>
            </section>

            {/* Contact Information */}
            <section className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-6'>
                <h2 className='text-[20px] leading-[36px] font-semibold mb-6'>連絡先情報</h2>
                <div className='space-y-6'>
                    <InfoRow label='所属会社名' value={profile.companyName} />
                    <InfoRow label='所属会社名（フリガナ）' value={profile.companyNameFuri} />
                    <InfoRow label='電話番号' value={profile.phoneNumber} />
                </div>
            </section>
        </div>
    );
});
